// src/app/core/service/business-group.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@enviroment/environment';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { AuthManagementService } from '@core/service/auth-management.service';
import { catchError, tap } from 'rxjs/operators';

type TokenShape =
  | string
  | { iv: string; cipherText: string } // por si el backend regresa token cifrado en dos partes
  ;

@Injectable({ providedIn: 'root' })
export class BusinessGroupService {
  private readonly LS_KEY = 'businessGroupId';

  private businessGroupIdSubject = new BehaviorSubject<string | null>(this.getInitialBusinessGroupId());
  public businessGroupId$ = this.businessGroupIdSubject.asObservable();

  constructor(
    private http: HttpClient,
    private httpMsgHandler: HttpMesageHandlerService,
    private auth: AuthManagementService
  ) {
    const stored = localStorage.getItem(this.LS_KEY);
    if (stored) {
      this.businessGroupIdSubject.next(stored);
    }
  }

  /** id actual (o null) */
  get currentId(): string | null {
    return this.businessGroupIdSubject.value;
  }

  /** Solo emite/guarda localmente (no pega al backend) */
  emitBusinessGroupId(id: string | null, persist = true): void {
    if (persist) {
      if (id) localStorage.setItem(this.LS_KEY, id);
      else localStorage.removeItem(this.LS_KEY);
    }
    this.businessGroupIdSubject.next(id);
  }

  private getInitialBusinessGroupId(): string | null {
    const stored = localStorage.getItem(this.LS_KEY);
    if (stored) return stored;

    const userData = this.auth.userData();
    const tokenGemp = userData?.IdGEMP ?? null;

    if (tokenGemp) {
      localStorage.setItem(this.LS_KEY, tokenGemp);
      return tokenGemp;
    }

    return null;
  }

  /**
   * Hace change-gemp y actualiza token + id.
   * Usa header 'X-Skip-Renew' para evitar que el interceptor renueve con token viejo.
   * Ajusta body si tu API espera otra forma (por ejemplo { idGEMP: id }).
   */
  changeGempAndRefreshToken(id: string): Observable<DefaultResponse<TokenShape>> {
    const token = this.auth.getUserKey();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Skip-Renew': '1',
    });

    return this.http
      .post<DefaultResponse<TokenShape>>(
        `${environment.backend_url}Auth/change-gemp`,
        JSON.stringify(id), // ⇐ si tu API requiere { idGEMP: id }, cambia aqui
        { headers }
      )
      .pipe(
        tap((res) => {
          const t = res.data;
          // Guarda el nuevo token en el formato correcto
          if (typeof t === 'string') {
            this.auth.saveUserKey(t);
          } else if (t && (t as any).iv && (t as any).cipherText) {
            this.auth.saveUserKey(`${t.iv}:${t.cipherText}`);
          }

          // persiste y emite el id
          this.emitBusinessGroupId(id, true);
        })
      );
  }

  /**
   * API principal para cambiar de grupo:
   * - Evita llamadas innecesarias si el id es falsy o igual al actual.
   * - Hace rollback del LS si falla.
   */
  setBusinessGroupId(id: string): void {
    if (!id) return; // guard 1
    const current = localStorage.getItem(this.LS_KEY);
    if (current === id) { // guard 2
      this.emitBusinessGroupId(id, false); // asegura emision
      return;
    }

    this.changeGempAndRefreshToken(id).pipe(
      catchError((err) => {
        // rollback visual
        if (current) localStorage.setItem(this.LS_KEY, current);
        else localStorage.removeItem(this.LS_KEY);
        this.httpMsgHandler.handleMsg(err);
        return throwError(() => err);
      })
    ).subscribe();
  }
}
